import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Share2, Printer, Link as LinkIcon } from 'lucide-react';

import { eventReportApi } from './eventReportApi';
import { EventReportView } from './EventReportView';
import { EventClosingCard } from './EventClosingCard';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { useT } from '@/lib/i18n/useT';
import { queryKeys } from '@/lib/api/queryKeys';
import { factsApi } from '@/features/insights/factsApi';

/**
 * Task 5.3 — the one Report surface.
 *
 * Reporting used to be spread over a Measure tab (redemption stats +
 * demographics + a button), a separate `/events/:id/report` page (the actual
 * report, print and share) and a shared public view. Three places, one
 * question. This is the whole thing, rendered inside the event's Report tab:
 * the report itself, prize redemption, cost per result, with Print and Share.
 *
 * Demographics are not repeated here — `EventReportView` already renders them
 * as one of its toggleable sections (task 5.4), and the Measure tab used to
 * show the same panel twice over.
 */
export function EventReportSurface({ eventId }: { eventId: number }) {
  const t = useT();
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['eventReport', 'internal', eventId],
    queryFn: () => eventReportApi.internal(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0,
  });

  // Los hechos del evento: lo que dibuja el embudo, el libro de premios, el
  // throughput y los costes. Van aparte del payload compartible porque ése
  // es público y esto lleva más detalle.
  const factsQ = useQuery({
    queryKey: queryKeys.insights.facts([eventId]),
    queryFn: () => factsApi.forEvents([eventId]),
    enabled: Number.isFinite(eventId) && eventId > 0,
    staleTime: 30_000,
  });
  const facts = factsQ.data?.events[0];
  const createShare = useMutation({
    mutationFn: () => eventReportApi.createShare(eventId),
    onSuccess: (res) => {
      const fullUrl = `${window.location.origin}${res.url}`;
      setShareUrl(fullUrl);
      navigator.clipboard?.writeText(fullUrl).catch(() => undefined);
    },
  });

  if (!Number.isFinite(eventId) || eventId <= 0) {
    return <p className="text-sm text-[var(--color-fg-muted)]">{t('eventReport.invalidId')}</p>;
  }
  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  return (
    <div className="space-y-6 print:p-0">
      {/* Controls only — never printed. */}
      <div className="flex flex-wrap items-center justify-end gap-2 print:hidden">
        <Button variant="secondary" onClick={() => window.print()}>
          <Printer className="mr-2 h-4 w-4" />
          {t('eventReport.print')}
        </Button>
        <Button
          variant="primary"
          onClick={() => createShare.mutate()}
          disabled={createShare.isPending}
        >
          <Share2 className="mr-2 h-4 w-4" />
          {t('eventReport.createShare')}
        </Button>
      </div>

      {shareUrl && (
        <div className="flex items-center gap-2 rounded-md border border-[var(--color-accent-ink)] bg-[var(--color-accent-soft)] p-3 text-sm print:hidden">
          <LinkIcon className="h-4 w-4 text-[var(--color-accent-ink)]" />
          <code className="flex-1 break-all">{shareUrl}</code>
          <span className="text-xs text-[var(--color-fg-muted)]">
            {t('eventReport.shareCopied')}
          </span>
        </div>
      )}

      {/* El evento terminó: lo que sobra vuelve al inventario con acta. Arriba
          del informe porque es lo primero que hay que hacer al cerrar. */}
      <EventClosingCard eventId={eventId} className="print:hidden" />

      <EventReportView report={data} variant="internal" facts={facts} />

    </div>
  );
}
